import {
  BIOME,
  CHUNK_SIZE,
  CHUNKS_PER_SIDE,
  classifyBiome,
  HEIGHT_SPACING,
  samplePlanGrid,
  WORLD_HALF,
  type Biome,
  type Plan,
  type TerrainField,
} from "@eldrun/worldgen";
import { mix32 } from "@eldrun/shared";

/** Vértices por lado de um chunk no LOD 0 (128 m / 2 m + 1). */
export const TILE_SIDE = CHUNK_SIZE / HEIGHT_SPACING + 1;

export const chunkOrigin = (c: number): number => -WORLD_HALF + c * CHUNK_SIZE;
export const chunkKey = (cx: number, cz: number): number => cz * CHUNKS_PER_SIDE + cx;

/**
 * Alturas de um chunk (TILE_SIDE² amostras a 2 m), geradas pela mesma função do heightfield
 * físico: os valores são idênticos aos do servidor (ADR 0004).
 */
export function generateTileHeights(field: TerrainField, cx: number, cz: number): Float32Array {
  const heights = new Float32Array(TILE_SIDE * TILE_SIDE);
  const x0 = chunkOrigin(cx);
  const z0 = chunkOrigin(cz);
  for (let j = 0; j < TILE_SIDE; j++) {
    const z = z0 + j * HEIGHT_SPACING;
    for (let i = 0; i < TILE_SIDE; i++) heights[j * TILE_SIDE + i] = field.height(x0 + i * HEIGHT_SPACING, z);
  }
  return heights;
}

/** Copia um tile para o heightfield global (bordas compartilhadas recebem o mesmo valor). */
export function blitTile(target: Float32Array, side: number, tile: Float32Array, cx: number, cz: number): void {
  const step = CHUNK_SIZE / HEIGHT_SPACING;
  for (let j = 0; j < TILE_SIDE; j++) {
    target.set(tile.subarray(j * TILE_SIDE, (j + 1) * TILE_SIDE), (cz * step + j) * side + cx * step);
  }
}

/** Grades do plano que o cliente usa para colorir (cópias leves, sem o campo de ruído). */
export type PlanGrids = Pick<Plan, "moisture" | "coastDistance" | "landDistance">;

/** Paleta base por bioma (doc 05, Direção de arte), em sRGB 0–255. */
export const BIOME_COLORS: Record<Biome, readonly [number, number, number]> = {
  [BIOME.mar]: [0xbf, 0xa3, 0x6a],
  [BIOME.praia]: [0xe8, 0xd4, 0x9a],
  [BIOME.mangue]: [0x5e, 0x53, 0x40],
  [BIOME.mata]: [0x3e, 0x6b, 0x2f],
  [BIOME.cerrado]: [0xc9, 0xb2, 0x5a],
  [BIOME.serra]: [0x8a, 0x85, 0x78],
};
const ROCK_DARK: readonly [number, number, number] = [0x5f, 0x5c, 0x55];
const DEEP_SAND: readonly [number, number, number] = [0x8f, 0x7c, 0x55];

/**
 * Cor por vértice: bioma classificado com as grades do plano (cosmético, não precisa ser
 * determinístico) + variação de ±4 % por hash do vértice global, que o shader interpola
 * (manchas suaves; as divisas entre biomas não serrilham).
 */
export function tileColors(heights: Float32Array, grids: PlanGrids, cx: number, cz: number): Uint8Array {
  const colors = new Uint8Array(TILE_SIDE * TILE_SIDE * 4);
  const x0 = chunkOrigin(cx);
  const z0 = chunkOrigin(cz);
  const step = CHUNK_SIZE / HEIGHT_SPACING;
  for (let j = 0; j < TILE_SIDE; j++) {
    for (let i = 0; i < TILE_SIDE; i++) {
      const k = j * TILE_SIDE + i;
      const y = heights[k] as number;
      // Diferença central no interior; unilateral na borda do tile (divisor correspondente).
      const il = Math.max(0, i - 1);
      const ir = Math.min(TILE_SIDE - 1, i + 1);
      const jd = Math.max(0, j - 1);
      const ju = Math.min(TILE_SIDE - 1, j + 1);
      const gx = ((heights[j * TILE_SIDE + ir] as number) - (heights[j * TILE_SIDE + il] as number)) / ((ir - il) * HEIGHT_SPACING);
      const gz = ((heights[ju * TILE_SIDE + i] as number) - (heights[jd * TILE_SIDE + i] as number)) / ((ju - jd) * HEIGHT_SPACING);
      const slope = Math.sqrt(gx * gx + gz * gz);
      const x = x0 + i * HEIGHT_SPACING;
      const z = z0 + j * HEIGHT_SPACING;
      const biome = classifyBiome({
        y,
        moisture: samplePlanGrid(grids.moisture, x, z),
        slope,
        coastDistance: samplePlanGrid(grids.coastDistance, x, z),
        landDistance: samplePlanGrid(grids.landDistance, x, z),
      });
      let base = BIOME_COLORS[biome];
      if (biome === BIOME.mar && y < -6) base = DEEP_SAND;
      if (biome === BIOME.serra && slope > 0.9) base = ROCK_DARK;
      const h = mix32((cz * step + j) * 4099 + (cx * step + i));
      const variation = 0.96 + ((h & 0xff) / 255) * 0.08;
      colors[k * 4] = Math.min(255, base[0] * variation);
      colors[k * 4 + 1] = Math.min(255, base[1] * variation);
      colors[k * 4 + 2] = Math.min(255, base[2] * variation);
      colors[k * 4 + 3] = biome;
    }
  }
  return colors;
}

export interface TileBounds {
  minY: number;
  maxY: number;
}

export function tileBounds(heights: Float32Array): TileBounds {
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < heights.length; i++) {
    const h = heights[i] as number;
    if (h < minY) minY = h;
    if (h > maxY) maxY = h;
  }
  return { minY, maxY };
}
